import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Share } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { useToast } from "../hooks/useToast";
import { apiClient } from "../lib/api";
import { getErrorInfo } from "../lib/errorUtils";
import { ShareModal } from "./ShareModal";
import { Button } from "./ui/button";

interface ShareButtonProps {
    storyId: string;
    storyTitle: string;
    className?: string;
}

function constructShareUrl(shareToken: string): string {
    return `${window.location.origin}/story/share/${shareToken}`;
}

export function ShareButton({ storyId, storyTitle, className }: ShareButtonProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const posthog = usePostHog();

    // Query to get current share status
    const {
        data: shareData,
        isLoading: isLoadingShare,
        error: shareError,
    } = useQuery({
        queryKey: ["storyShare", storyId],
        queryFn: () => apiClient.getStoryShare(storyId),
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Mutation to create share link
    const createShareMutation = useMutation({
        mutationFn: () => apiClient.createStoryShare(storyId),
        onSuccess: (data) => {
            // Track story shared event for analytics
            posthog.capture("story_shared", {
                story_id: storyId,
                share_method: "link",
                share_token: data.share.share_token,
                timestamp: new Date().toISOString(),
            });

            queryClient.setQueryData(["storyShare", storyId], {
                hasShare: true,
                share: data.share,
            });
            // Open modal immediately after creating share link
            setIsModalOpen(true);
        },
        onError: (error) => {
            console.error("Failed to create share link:", error);
            const errorInfo = getErrorInfo(error);
            toast({
                title: "Failed to create share link",
                description: errorInfo.message,
                variant: "destructive",
            });
        },
    });

    // Mutation to revoke share link
    const revokeShareMutation = useMutation({
        mutationFn: () => apiClient.revokeStoryShare(storyId),
        onSuccess: () => {
            queryClient.setQueryData(["storyShare", storyId], {
                hasShare: false,
            });
            toast({
                title: "Share link revoked",
                description: "Your story is no longer publicly accessible.",
                variant: "success",
            });
            setIsModalOpen(false);
        },
        onError: (error) => {
            const errorInfo = getErrorInfo(error);
            toast({
                title: "Failed to revoke share link",
                description: errorInfo.message,
                variant: "destructive",
            });
        },
    });

    const handleShareClick = () => {
        if (shareData?.hasShare) {
            // Already has share link, open modal directly
            setIsModalOpen(true);
        } else {
            // Create share link and modal will open on success
            createShareMutation.mutate();
        }
    };

    const handleRevokeShare = () => {
        revokeShareMutation.mutate();
    };

    if (shareError) {
        return null; // Hide button if there's an error
    }

    const shareUrl = shareData?.share?.share_token ? constructShareUrl(shareData.share.share_token) : "";

    return (
        <>
            <Button
                onClick={handleShareClick}
                variant="outline"
                size="lg"
                disabled={isLoadingShare || createShareMutation.isPending}
                className={`gap-2 ${className} ${
                    shareData?.hasShare
                        ? "border-success text-success hover:border-success hover:text-success hover:bg-success/10"
                        : ""
                }`}
            >
                <Share className="h-4 w-4" />
                {isLoadingShare
                    ? "Loading..."
                    : createShareMutation.isPending
                      ? "Creating Link..."
                      : shareData?.hasShare
                        ? "Story Shared"
                        : "Share Story"}
            </Button>

            {shareData?.hasShare && (
                <ShareModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    shareUrl={shareUrl}
                    storyTitle={storyTitle}
                    onRevoke={handleRevokeShare}
                    isRevoking={revokeShareMutation.isPending}
                />
            )}
        </>
    );
}
